import { createContext, type ReactNode, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { type EditorHistoryOptions, useEditorHistory } from "../hooks/use-editor-history";


interface EditorHistoryContextValue {
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  captureState: () => void;
  startTransaction: () => void;
  commitTransaction: () => void;
  clearHistory: () => void;
  historySize: number;
}


const EditorHistoryContext = createContext<EditorHistoryContextValue | null>(null);


interface EditorHistoryProviderProps {
  children: ReactNode;
  options?: EditorHistoryOptions;
}


export function EditorHistoryProvider({ children, options }: EditorHistoryProviderProps) {
  const form = useFormContext();
  const history = useEditorHistory(form, options);

  return (
    <EditorHistoryContext.Provider value={history}>
      {children}
    </EditorHistoryContext.Provider>
  );
}


export function useEditorHistoryContext() {
  const context = useContext(EditorHistoryContext);
  if (!context) {
    throw new Error("useEditorHistoryContext must be used within EditorHistoryProvider");
  }
  return context;
}


// Optional version that returns null if not available
export function useEditorHistoryContextOptional() {
  return useContext(EditorHistoryContext);
}
